import { Spacing } from '@persona-id/design-tokens';
import { uniq } from 'lodash';
import { COLORS } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  MatchIconAlert,
  MatchIconDismissed,
  MatchIconPositive,
} from 'dashboard/components/ReportResult/MatchIcons';
import {
  SourceMatchEntity,
  translateWatchlistBirthdatesForDisplay,
} from 'dashboard/models/report/watchlist';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { paragraphMixins } from 'design-system/mixins/text';
import theme from 'design-system/theme';
import IconChevron from 'images/icons/chevron.svg';

const StyledIconChevron = styled(IconChevron)`
  width: ${Spacing[5]};
  flex-shrink: 0;
  align-self: center;
`;

const Container = styled.div`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: ${Spacing[5]} 1fr ${Spacing[5]};
  padding: ${Spacing[2]} 0;
`;

const MatchSummaryContent = styled('div')`
  flex-grow: 1;
`;

const MatchTitle = styled('div')`
  ${paragraphMixins.lgBold};
  color: ${theme.color.text.primary};
  line-height: 26px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const Subtext = styled('div')`
  ${paragraphMixins.md};
  display: flex;
  color: ${COLORS.gray70};
`;

const Stickers = styled('div')`
  display: flex;
  flex-wrap: wrap;
`;

const StyledSticker = styled(Sticker)`
  margin-right: ${Spacing[2]};
  margin-bottom: ${Spacing[2]};
`;

const WatchlistSourceEntityMatchSummary = ({
  entity,
  isDismissed,
}: {
  entity: SourceMatchEntity;
  isDismissed?: boolean;
}) => {
  const { names, watchlists, birthdates } = entity;
  const name = names?.[0] ?? '';
  const displayTags = uniq((watchlists ?? []).flatMap((watchlist) => watchlist['tags'])).filter(
    Boolean,
  );
  const subText = birthdates ? translateWatchlistBirthdatesForDisplay(birthdates).join(' • ') : '';

  let icon;
  if (isDismissed) {
    icon = <MatchIconDismissed />;
  } else if (entity.watchlists?.length === 0) {
    icon = <MatchIconPositive />;
  } else {
    icon = <MatchIconAlert />;
  }

  return (
    <Container>
      {icon}
      <MatchSummaryContent>
        <MatchTitle>{name}</MatchTitle>
        <Spacer height={1} />
        <Subtext>{subText}</Subtext>
        <Spacer height={1} />
        <Stickers>
          {Object.entries(displayTags).map(([t, tag]) => (
            <StyledSticker key={t}>{tag?.toUpperCase()}</StyledSticker>
          ))}
        </Stickers>
      </MatchSummaryContent>
      <StyledIconChevron />
    </Container>
  );
};

export default WatchlistSourceEntityMatchSummary;
